import { Alert, Button, Modal, Text } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { B2BPaymentMethod, CorePaymentMethod } from "@oysterhr/schema"
import { useAuthorizePaymentMethod } from "../hooks/useAuthorizeDirectDebitOnPaymentMethod"
import { PAYMENTS_COMPANY_GET_PAYMENT_METHODS } from "../hooks/useNewCompanyPaymentMethods"
import { formatAddressToSingleLine } from "../utils"

const FieldCell = ({ name, value }: { name: string; value?: string | null }) => {
  if (!value) return null

  return (
    <div className="flex flex-col">
      <Text className="font-semibold">{name}</Text>
      <Text>{value}</Text>
    </div>
  )
}
interface AuthorizeDirectDebitModalProps {
  isOpen: boolean
  paymentMethod?: CorePaymentMethod | B2BPaymentMethod
  onClose: () => void
}

export const AuthorizeDirectDebitModal = ({
  isOpen,
  paymentMethod,
  onClose,
}: AuthorizeDirectDebitModalProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "authorizationStep",
  })

  const [authorize, { loading }] = useAuthorizePaymentMethod()

  const onAuthorize = () => {
    authorize({
      variables: { authorized: true, paymentMethodId: paymentMethod?.id as string },
      refetchQueries: [PAYMENTS_COMPANY_GET_PAYMENT_METHODS],
      onCompleted: () => {
        onClose()
      },
    })
  }

  if (!paymentMethod) return null

  const isFTEPaymentMethod =
    paymentMethod.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA ||
    paymentMethod.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH

  const isSepa =
    paymentMethod.__typename === PaymentMethodEnumType.STRIPE_SEPA ||
    paymentMethod.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Header>{t("title")}</Modal.Header>
      <Modal.Content>
        <div className="text-left">
          <Text>{t("content")}</Text>
          {isFTEPaymentMethod && (
            <Alert variation="info" className="mt-4">
              <Alert.Icon />
              <Alert.Content>{t("alert")}</Alert.Content>
            </Alert>
          )}
          {paymentMethod && (
            <div className="mt-4 space-y-4">
              <FieldCell
                name={t("accountHolderName")}
                value={
                  "accountHolderName" in paymentMethod ? paymentMethod.accountHolderName : null
                }
              />
              <FieldCell
                name={t("accountIBAN")}
                value={`****${paymentMethod.accountNumber.slice(-4)}`}
              />
              <FieldCell
                name={t("accountAddress")}
                value={isSepa ? formatAddressToSingleLine(paymentMethod.billingAddress) : null}
              />
            </div>
          )}
          <div className="mt-8 flex space-x-8">
            <Button variation="borderless" onClick={onClose}>
              {t("btnCancel")}
            </Button>
            <Button onClick={onAuthorize} isLoading={loading}>
              {t("btnConfirm")}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}
